var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo } from 'react';
import { useSetState } from 'react-use';
import { aiApi } from 'apis/ai';
const initialResult = {
    asyncStatus: 'uninitialized',
    translatedText: undefined,
    originalArg: undefined,
    error: undefined,
};
/**
 * A hook for translating text to a given language.
 *
 * @remarks
 *   Because the translation happens through LLM, it is not necessary to provide
 *   the source language code.
 * @example
 *   const [translate] = useTranslateText();
 *
 *   // In some function
 *   try {
 *     const response = await translate({
 *       languageCode: 'fr',
 *       text: 'Hello world!',
 *     });
 *     // do something with `response.translatedText`
 *   } catch (error) {
 *     // handle error
 *   }
 *
 * @example
 *   const [translate, { asyncStatus, translatedText, error }] =
 *     useTranslateText();
 *
 *   // In some function
 *   translate({
 *     languageCode: 'fr',
 *     text: 'Hello world!',
 *   }).catch((error) => console.error(error));
 *
 *   // meanwhile in a hook
 *   useEffect(() => {
 *     if (asyncStatus === 'rejected') {
 *       // handle error
 *     }
 *   }, [asyncStatus]);
 */
export function useTranslateText() {
    const [response, setResponse] = useSetState(initialResult);
    const [createTranslation] = aiApi.useCreateAiTextTranslationMutation();
    const initiate = useCallback((arg) => __awaiter(this, void 0, void 0, function* () {
        setResponse(initialResult);
        try {
            setResponse({ asyncStatus: 'pending' });
            const createTranslationResponse = yield createTranslation(arg).unwrap();
            const { text: translatedText } = createTranslationResponse;
            setResponse({
                asyncStatus: 'fulfilled',
                translatedText,
                originalArg: arg,
            });
            return Promise.resolve({
                translatedText,
            });
        }
        catch (error) {
            setResponse({ asyncStatus: 'rejected', error, originalArg: arg });
            return Promise.reject(error);
        }
    }), [createTranslation, setResponse]);
    return useMemo(() => [initiate, response], [initiate, response]);
}
